import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ZodiacSelector from '../components/ZodiacSelector';
import Loading from '../components/Loading';
import ShareReading from '../components/ShareReading';
import ReadingDisclaimer from '../components/ReadingDisclaimer';
import { getDailyHoroscope, isGeminiReady } from '../services/geminiService';
import { useLanguage } from '../context/LanguageContext';

const periodKeys = ['daily', 'weekly', 'monthly', 'yearly'];

export default function Horoscope() {
  const [searchParams] = useSearchParams();
  const [selectedSign, setSelectedSign] = useState(searchParams.get('sign') || '');
  const [period, setPeriod] = useState('daily');
  const [reading, setReading] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { language, t } = useLanguage();

  useEffect(() => {
    const signFromUrl = searchParams.get('sign');
    if (signFromUrl) setSelectedSign(signFromUrl);
  }, [searchParams]);

  const handleGetHoroscope = async () => {
    if (!selectedSign) return;
    if (!isGeminiReady()) {
      setError(t('apiKeyError'));
      return;
    }
    setLoading(true);
    setError('');
    setReading('');
    try {
      const result = await getDailyHoroscope(selectedSign, period, language);
      setReading(result);
    } catch (err) {
      setError(err.message || 'Failed to get horoscope. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="container">
        <div className="page-header">
          <h2>{t('horoscopeTitle')}</h2>
          <p>{t('horoscopeDesc')}</p>
        </div>

        {/* Period Tabs */}
        <div className="tabs">
          {periodKeys.map(p => (
            <button
              key={p}
              className={`tab ${period === p ? 'active' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {t(p)}
            </button>
          ))}
        </div>

        {/* Zodiac Selector */}
        <ZodiacSelector selectedSign={selectedSign} onSelect={setSelectedSign} />

        {/* Get Horoscope Button */}
        {selectedSign && (
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <button
              className="btn btn-primary"
              onClick={handleGetHoroscope}
              disabled={loading}
            >
              {loading ? t('readingStars') : t('getHoroscope', { period: t(period), sign: selectedSign })}
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && <Loading message={t('loadingHoroscope', { period: t(period), sign: selectedSign })} />}

        {/* Error */}
        {error && <div className="error-message">{error}</div>}

        {/* Result */}
        {reading && (
          <div className="result-container">
            <div className="result-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3>{t('horoscopeResult', { period: t(period), sign: selectedSign })}</h3>
                <ShareReading title={`${t(period)} Horoscope for ${selectedSign}`} content={reading} type="horoscope" />
              </div>
              <div className="content">{reading}</div>
              <ReadingDisclaimer />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
